import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { ConversationHistoryResponse } from '@/types/conversation-history';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

// codex-review .contract-lens r15 F1: ConversationMessage/ConversationCorrespondent/
// ConversationHistoryResult used to be declared independently here, a near-duplicate of the
// server's own local ConversationHistoryResponse in api/os/linkedin/conversation-history.ts, with
// no shared type or runtime check linking the two — the exact gap that let the r12 F2 `success`
// field defect happen (added to one branch, silently absent from three others). Re-exported from
// the shared src/types/conversation-history.ts so thread-timeline.tsx's existing `import type {
// ConversationMessage } from '@/hooks/useConversationHistory'` keeps working unchanged.
export type {
  ConversationMessage,
  ConversationCorrespondent,
  ConversationHistoryResponse as ConversationHistoryResult,
} from '@/types/conversation-history';

// ─────────────────────────────────────────────────────────────────────────────
// Hook: Fetch full conversation history from HeyReach
// ─────────────────────────────────────────────────────────────────────────────

export const useConversationHistory = (
  threadId: string | undefined,
  enabled: boolean = false
): ReturnType<typeof useQuery<ConversationHistoryResponse>> => {
  return useQuery<ConversationHistoryResponse>({
    queryKey: ['conversation-history', threadId],
    queryFn: async (): Promise<ConversationHistoryResponse> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch('/api/os/linkedin/conversation-history', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ threadId }),
      });

      // 404 = conversation doesn't exist in HeyReach — return empty, don't error
      if (response.status === 404) {
        return { messages: [], totalMessages: 0, correspondent: null };
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = (errorData as Record<string, unknown>)?.message
          ? String((errorData as Record<string, unknown>).message)
          : `Failed to fetch conversation history (${response.status})`;
        throw new Error(message);
      }

      return response.json() as Promise<ConversationHistoryResponse>;
    },
    enabled: !!threadId && enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false,
  });
};
